import React from 'react';
import PropTypes from 'prop-types';
import { Cushion } from '@pitchero/react-ui';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import TEAM_PROP_TYPES from '../../lib/prop-types/team';
import ActivityStream from '../activity-stream';
import NoBackground from '../club-frame/header/no-background';
import Pagination from '../pagination';
import SeoHead from '../seo';
import RosBottomBanner from '../adverts/ros-bottom-banner';
import TopBannerVideoPlayerSwitch from '../adverts/top-banner-video-player-switch';
import FilterBar from './filter-bar';
import MediaTypeTabs from './media-type-tabs';
import NoResults from './no-results';

const NUMBER_OF_ITEMS_BEFORE_ADVERT = 12;

const MediaList = ({
  club,
  isLoading,
  mediaType,
  messages,
  onChangeTeamFilter,
  onSearchChange,
  page,
  pagination,
  search,
  team,
  t,
}) => {
  const paginationRouteParams = {};
  if (team) {
    paginationRouteParams.teamId = team.id;
  }

  if (search.length) {
    paginationRouteParams.search = search;
  }

  let titleTranslationKey = `media:page_title_${mediaType}`;
  const descriptionTranslationKey = `media:meta_description_${mediaType}`;
  const translationValues = {};

  if (team) {
    translationValues.teamName = team.name;
  }

  if (search) {
    titleTranslationKey += '_search';
  } else if (pagination && pagination.totalPages && pagination.totalPages > 1) {
    titleTranslationKey += '_paginated';
    translationValues.page = pagination.page;
    translationValues.totalPages = pagination.totalPages;
  }

  const { advertisingTier, sportId } = club;

  return (
    <>
      <SeoHead
        club={club}
        descriptionTranslationKey={descriptionTranslationKey}
        titleTranslationKey={titleTranslationKey}
        translationValues={translationValues}
        route={`media.${mediaType}`}
      />
      <NoBackground />
      <FilterBar
        club={club}
        mediaType={mediaType}
        onChangeTeamFilter={onChangeTeamFilter}
        onSearchChange={onSearchChange}
        search={search}
        team={team}
        t={t}
      />
      <MediaTypeTabs mediaType={mediaType} teamId={team ? team.id : null} search={search} />
      <Cushion top="medium">
        <TopBannerVideoPlayerSwitch
          advertisingTier={advertisingTier}
          pageType={`media.list.${mediaType}`}
          sportId={sportId}
          uid={mediaType}
        />
      </Cushion>
      <Cushion
        vertical="medium"
        responsive={[
          { minWidth: 'palm', props: { horizontal: 'medium' } },
          { minWidth: 'lap', props: { horizontal: 'large' } },
        ]}
        component="div"
      >
        {messages.length > 0 || isLoading ? (
          <>
            <ActivityStream
              isLoading={isLoading}
              messages={messages.slice(0, NUMBER_OF_ITEMS_BEFORE_ADVERT)}
              placeholderLimit={NUMBER_OF_ITEMS_BEFORE_ADVERT}
            />

            <Cushion top="small" responsive={[{ minWidth: 'tab', props: { top: 'large' } }]}>
              <RosBottomBanner key={mediaType} />
            </Cushion>

            <Cushion
              component="div"
              top="small"
              responsive={[{ minWidth: 'tab', props: { top: 'large' } }]}
            >
              <ActivityStream
                isLoading={isLoading}
                messages={messages.slice(NUMBER_OF_ITEMS_BEFORE_ADVERT)}
                placeholderLimit={0}
              />
            </Cushion>

            {pagination && (
              <Cushion vertical="large" component="div" style={{ textAlign: 'center' }}>
                <Pagination
                  {...pagination}
                  page={page}
                  route={team ? `media.${mediaType}.team` : `media.${mediaType}`}
                  routeParams={paginationRouteParams}
                />
              </Cushion>
            )}
          </>
        ) : (
          <NoResults mediaType={mediaType} team={team} search={search} />
        )}
      </Cushion>
    </>
  );
};

MediaList.defaultProps = {
  pagination: null,
  team: null,
};

MediaList.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  isLoading: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  mediaType: PropTypes.oneOf(['albums', 'videos']).isRequired,
  page: PropTypes.number.isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    perPage: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    totalResults: PropTypes.number.isRequired,
  }),
  onChangeTeamFilter: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  team: TEAM_PROP_TYPES,
};

export default MediaList;
